// Enhanced Profile Management Page with improved UI/UX
// Features skeleton loading, optimistic updates, and micro-interactions

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../hooks/useAuth';
import { useFocusManagement, useErrorFocus } from '../hooks/useFocusManagement';
import { useOptimisticUpdate } from '../hooks/useOptimisticUpdate';
import { userService } from '../services/api';
import { profileFormSchema, type ProfileFormData } from '../lib/schemas';
import { UserAvatar, LoadingSpinner } from '../components/ui';
import { UserBadgeList } from '../components/badges';
import { useUserBadgeDisplay } from '../hooks/useUserBadges';
import { useSubscription } from '../services/subscription/hooks';
import { DisplayNameField } from '../components/form';
import { ProfilePageSkeleton } from '../components/ui/ProfilePageSkeleton';
import { AnimatedMessage } from '../components/ui/AnimatedMessage';
import { Skeleton } from '../components/ui/LoadingSpinner';
import type { PhotoUrls } from '../lib/types/user';
import type { HeapsterUser } from '../contexts/AuthContext';

export function ProfilePage() {
  const { user, isEmailVerified, refreshUserProfile } = useAuth();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Badge data for the current user
  const badgeData = useUserBadgeDisplay();

  // Subscription and entitlements data
  const {
    subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useSubscription();

  const { containerRef } = useFocusManagement({
    autoFocusFirst: true,
  });

  const { focusFirstError } = useErrorFocus();

  // Optimistic update hook for immediate UI feedback
  const { data: optimisticUser, optimisticUpdate } =
    useOptimisticUpdate<HeapsterUser | null>(user, {
      onError: error => {
        console.error('Optimistic update failed:', error);
        // Refresh user data to ensure sync
        void refreshUserProfile?.();
      },
    });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      display_name: user?.display_name ?? '',
    },
  });

  const displayName = watch('display_name');

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const onSubmit = async (data: ProfileFormData) => {
    if (!user) return;

    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      await optimisticUpdate(data, async () => {
        const result = await userService.updateUserProfile(user.uid, {
          display_name: data.display_name,
        });

        if (!result.success) {
          throw new Error(result.error ?? 'Failed to update profile');
        }

        // Refresh auth context to sync with backend
        await refreshUserProfile?.();
      });

      setSubmitMessage({
        type: 'success',
        text: 'Profile updated successfully!',
      });
      reset(data); // Reset form with new values to clear isDirty state
    } catch (error) {
      console.error('Profile update error:', error);
      setSubmitMessage({
        type: 'error',
        text:
          error instanceof Error
            ? error.message
            : 'Failed to update profile. Please try again.',
      });
      focusFirstError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAvatarUpload = async (photoUrls: PhotoUrls) => {
    if (!user) return;

    setAvatarUploading(true);
    try {
      // The avatar trigger has already written `photo_url`, so there is no
      // profile PUT to send (#2325); only reload the stored profile.
      await optimisticUpdate({ photo_url: photoUrls }, async () => {
        await refreshUserProfile?.();
      });

      setSubmitMessage({
        type: 'success',
        text: 'Profile photo updated successfully!',
      });
    } catch (error) {
      console.error('Avatar update error:', error);
      setSubmitMessage({
        type: 'error',
        text:
          error instanceof Error
            ? error.message
            : 'Failed to update profile photo. Please try again.',
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarError = (error: Error) => {
    setSubmitMessage({
      type: 'error',
      text: error.message,
    });
  };

  // Show skeleton during initial load
  if (isInitialLoading || !user) {
    return <ProfilePageSkeleton />;
  }

  // Use optimistic user data for display
  const displayUser = optimisticUser ?? user;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Profile Management
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/dashboard"
                className="btn-secondary hover:scale-105 transition-transform duration-200"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div ref={containerRef} className="space-y-8">
          {/* Alert Banners */}
          <div className="space-y-4">
            {/* Submit Message with Animation */}
            {submitMessage && (
              <AnimatedMessage
                type={submitMessage.type}
                message={submitMessage.text}
                onDismiss={() => setSubmitMessage(null)}
              />
            )}
          </div>

          {/* Profile Form */}
          <div className="card transition-shadow hover:shadow-lg duration-300">
            <div className="card-header">
              <h2 className="card-title">Basic Information</h2>
            </div>

            <form onSubmit={e => void handleSubmit(onSubmit)(e)}>
              <div className="card-content space-y-6">
                {/* Profile Avatar Section */}
                <div className="flex flex-col sm:flex-row items-center sm:space-x-6 space-y-4 sm:space-y-0">
                  <div className="flex-shrink-0 transition-transform hover:scale-105 duration-200">
                    <UserAvatar
                      displayName={displayUser.display_name}
                      photoUrls={displayUser.photo_url}
                      size="xl"
                      editable={true}
                      userId={displayUser.uid}
                      onUploadComplete={urls => void handleAvatarUpload(urls)}
                      onUploadError={handleAvatarError}
                    />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Profile Photo
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      Click on your avatar to upload a new photo. Images will be
                      automatically resized.
                    </p>
                    {avatarUploading && (
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        <LoadingSpinner
                          size="sm"
                          message="Updating profile photo..."
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Display Name Field with Enhanced Focus States */}
                <div className="transition-all duration-200">
                  <DisplayNameField
                    value={displayName ?? ''}
                    onChange={value =>
                      setValue('display_name', value, { shouldDirty: true })
                    }
                    placeholder="Enter your display name"
                    disabled={isSubmitting}
                    required={true}
                    id="display_name"
                    label="Display Name"
                    helpText="This is how your name will appear to other users"
                    className="focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-all duration-200"
                  />
                  {errors.display_name && (
                    <div className="mt-2 text-sm text-red-600 dark:text-red-400 animate-shake">
                      {errors.display_name.message}
                    </div>
                  )}
                </div>

                {/* Account Information (Read-only) */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Account Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <div className="input bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                        {displayUser.email}
                      </div>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Contact support to change your email address
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-footer flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                <Link
                  to="/dashboard"
                  className="btn-secondary w-full sm:w-auto order-2 sm:order-1 hover:scale-105 transition-transform duration-200"
                >
                  Cancel
                </Link>
                <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto order-1 sm:order-2">
                  {isDirty && (
                    <span className="text-sm text-amber-600 dark:text-amber-400 animate-pulse">
                      You have unsaved changes
                    </span>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting || !isDirty}
                    className="btn-primary w-full sm:w-auto hover:scale-105 transition-all duration-200 disabled:scale-100"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <LoadingSpinner size="sm" className="mr-2" />
                        Saving...
                      </span>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Your Badges Section */}
          <div className="card transition-shadow hover:shadow-lg duration-300">
            <div className="card-header">
              <h2 className="card-title">Your Badges</h2>
            </div>
            <div className="card-content">
              <UserBadgeList
                userBadges={badgeData.userBadges}
                badgeDefinitions={badgeData.badgeDefinitions}
                isLoading={badgeData.isLoading}
                error={badgeData.error}
                className="max-w-none"
              />
            </div>
          </div>

          {/* Your Entitlements Section */}
          <div className="card transition-shadow hover:shadow-lg duration-300">
            <div className="card-header">
              <h2 className="card-title">Your Entitlements</h2>
            </div>
            <div className="card-content">
              {subscriptionLoading ? (
                // Loading state
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full rounded-lg" />
                  <Skeleton className="h-24 w-full rounded-lg" />
                </div>
              ) : subscriptionError ? (
                // Error state
                <div className="text-center py-8">
                  <p className="text-red-600 dark:text-red-400">
                    Failed to load entitlements. Please try again later.
                  </p>
                </div>
              ) : !subscription?.entitlements ||
                Object.keys(subscription.entitlements).length === 0 ? (
                // Empty state
                <div className="text-center py-8">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                    <svg
                      className="h-6 w-6 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    No active entitlements.
                  </p>
                </div>
              ) : (
                // Display entitlements in grid
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(subscription.entitlements).map(
                    ([entitlementId, entitlement]) => (
                      <div
                        key={entitlementId}
                        className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            {entitlement.name || entitlementId}
                          </h3>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {entitlement.sources?.map(source => (
                            <span
                              key={source}
                              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                                source === 'subscription'
                                  ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                                  : 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300'
                              }`}
                            >
                              {source === 'subscription'
                                ? 'Subscription'
                                : 'Manual'}
                            </span>
                          ))}
                        </div>
                        {/* An end only when the data gives one (#2463): a
                            null expiry has none, and a renewing Stripe
                            subscription's date is when it renews. */}
                        {entitlement.periodEndDate &&
                          !(
                            entitlement.hasStripeSubscription &&
                            entitlement.willRenew
                          ) && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Expires:{' '}
                              {new Date(
                                entitlement.periodEndDate
                              ).toLocaleDateString()}
                            </div>
                          )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Additional Settings with Hover Effects */}
          <div className="card transition-shadow hover:shadow-lg duration-300">
            <div className="card-header">
              <h2 className="card-title">Additional Settings</h2>
            </div>
            <div className="card-content">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/settings"
                  className="flex items-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group hover:scale-105 transform"
                >
                  <div className="flex-shrink-0 transition-transform group-hover:rotate-12 duration-300">
                    <svg
                      className="w-6 h-6 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200">
                      Account Settings
                    </h3>
                  </div>
                </Link>

                {!isEmailVerified && (
                  <Link
                    to="/verify-email"
                    className="flex items-center p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all duration-200 group hover:scale-105 transform"
                  >
                    <div className="flex-shrink-0 transition-transform group-hover:scale-110 duration-300">
                      <svg
                        className="w-6 h-6 text-amber-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        Verify Email
                      </h3>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
